import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Plus, Building2, Search, X, CheckCircle2,
  Calendar, AlertCircle, ChevronRight, Archive, RotateCw, Trash2,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "@/api";


/**
 * Étape 2 (Immeuble) — Liste des dossiers immeuble DÉCLARATIFS.
 * Lit GET /api/immeuble-dossiers et affiche en tableau.
 * Bouton "+ Nouveau dossier" → wizard 2 étapes (sélection Master → champs globaux fiscaux).
 *
 * ⚠️ ZÉRO LIEN avec le module Immeuble opérationnel (baux TAL, dépenses, etc.).
 */
export default function ImmeubleDossiersList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [mastersById, setMastersById] = useState({});
  const [loading, setLoading] = useState(true);
  const [archivedFilter, setArchivedFilter] = useState(false);
  const [search, setSearch] = useState("");
  const [showWizard, setShowWizard] = useState(false);
  // Batch #14.128 — Actions sur tuiles archivées (Restaurer / Supprimer définitivement)
  const [actioningId, setActioningId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/immeuble-dossiers`, {
        headers: authH(),
        params: { archived: archivedFilter },
      });
      const list = r.data?.items || [];
      setItems(list);
      const uniqIds = [...new Set(list.map((d) => d.master_id))];
      const mb = {};
      await Promise.all(uniqIds.map(async (mid) => {
        try {
          const mr = await axios.get(`${API}/masters/${mid}`, { headers: authH() });
          mb[mid] = mr.data;
        } catch { /* skip */ }
      }));
      setMastersById(mb);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [archivedFilter]);

  useEffect(() => { load(); }, [load]);

  // Batch #14.128 — Archive un dossier actif
  const handleArchive = async (it, masterName) => {
    if (!window.confirm(
      `Archiver le dossier immeuble de "${masterName}" ?\n\n` +
      `Il sera caché de la liste active. Réactivable depuis l'onglet "Voir archives".`
    )) return;
    setActioningId(it.id);
    try {
      await axios.delete(`${API}/immeuble-dossiers/${it.id}`, { headers: authH() });
      toast.success("Dossier archivé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec d'archivage");
    } finally {
      setActioningId(null);
    }
  };

  // Batch #14.128 — Restaure un dossier archivé
  const handleRestore = async (it, masterName) => {
    if (!window.confirm(`Réactiver le dossier immeuble de "${masterName}" ?`)) return;
    setActioningId(it.id);
    try {
      await axios.post(`${API}/immeuble-dossiers/${it.id}/restore`, {}, { headers: authH() });
      toast.success("Dossier réactivé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de réactivation");
    } finally {
      setActioningId(null);
    }
  };

  // Batch #14.128 — Suppression DÉFINITIVE (depuis archives uniquement)
  const handleHardDelete = async (it, masterName) => {
    if (!window.confirm(
      `⚠️ SUPPRIMER DÉFINITIVEMENT le dossier immeuble de "${masterName}" ?\n\n` +
      `Cette action est IRRÉVERSIBLE. Le dossier sera retiré de la base de données.\n\n` +
      `Le Master n'est PAS touché — il restera disponible pour créer un nouveau dossier.`
    )) return;
    setActioningId(it.id);
    try {
      await axios.delete(
        `${API}/immeuble-dossiers/${it.id}?hard=true&confirm=SUPPRIMER`,
        { headers: authH() },
      );
      toast.success("Dossier supprimé définitivement");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de suppression");
    } finally {
      setActioningId(null);
    }
  };

  const filtered = items.filter((it) => {
    if (!search) return true;
    const m = mastersById[it.master_id];
    const name = (m?.couple_display_name || "").toLowerCase();
    return name.includes(search.toLowerCase()) || it.id.includes(search) || (it.neq || "").includes(search);
  });

  const countApts = (d) => (d.properties || []).reduce((s, p) => s + (p.apartments?.length || 0), 0);

  return (
    <div className="space-y-4" data-testid="immeuble-dossiers-list">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-slate-800">
            <Building2 className="w-6 h-6 text-emerald-600" /> Dossier Immeuble — Déclaratif
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Entités indépendantes liées à un Master · purement informatives.{" "}
            {items.length} dossier{items.length > 1 ? "s" : ""} {archivedFilter ? "archivé(s)" : "actif(s)"}.
          </p>
          <p className="text-[11px] text-slate-400 mt-0.5 italic">
            Aucun lien avec le portail opérationnel (baux TAL, RL-31, dépenses, coffre-fort).
          </p>
        </div>
        <button
          onClick={() => setShowWizard(true)}
          className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-emerald-700 inline-flex items-center gap-2"
          data-testid="immeuble-list-new-btn"
        >
          <Plus className="w-4 h-4" /> Nouveau dossier immeuble
        </button>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par Master ou NEQ…"
            className="pl-9 pr-3 py-2 border rounded-lg text-sm w-72"
            data-testid="immeuble-list-search"
          />
        </div>
        <label className="inline-flex items-center gap-1.5 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={archivedFilter}
            onChange={(e) => setArchivedFilter(e.target.checked)}
            data-testid="immeuble-list-archived-toggle"
          />
          Voir archives
        </label>
      </div>

      {/* Tuiles (Batch #14.65 — vue tuile remplace la vue liste) */}
      {loading ? (
        <div className="bg-white border border-slate-200 rounded-xl text-center py-16">
          <Loader2 className="inline w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl text-center py-16" data-testid="immeuble-list-empty">
          <Building2 className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500 mb-1">Aucun dossier immeuble {archivedFilter ? "archivé" : "actif"}</p>
          <p className="text-xs text-slate-400">
            Cliquez sur "+ Nouveau dossier immeuble" pour créer le premier dossier.
          </p>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4"
          data-testid="immeuble-list-tiles"
        >
          {filtered.map((it) => {
            const m = mastersById[it.master_id];
            const masterName = m?.couple_display_name || "(master inconnu)";
            const nProps = (it.properties || []).length;
            const nApts = countApts(it);
            const isArchived = !!it.is_archived;
            const isBusy = actioningId === it.id;

            return (
              <div
                key={it.id}
                className={`group relative bg-white border border-slate-200 rounded-xl p-4 transition-all duration-150 ${
                  isArchived
                    ? "opacity-80 hover:border-amber-300"
                    : "hover:border-emerald-400 hover:shadow-lg hover:-translate-y-0.5 cursor-pointer"
                }`}
                onClick={() => {
                  if (!isArchived) navigate(`/admin/immeuble-dossiers/${it.id}`);
                }}
                data-testid={`immeuble-list-tile-${it.id}`}
              >
                {/* Badge ARCHIVÉ en coin */}
                {isArchived && (
                  <span
                    className="absolute top-2 right-2 px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[9px] font-bold tracking-wider"
                    data-testid={`immeuble-list-archived-badge-${it.id}`}
                  >
                    ARCHIVÉ
                  </span>
                )}

                {/* En-tête : icône + chiffres */}
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="flex gap-3 text-right mr-12">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Propr.</div>
                      <div className="text-lg font-mono font-bold text-slate-800 leading-none">{nProps}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Log.</div>
                      <div className="text-lg font-mono font-bold text-slate-800 leading-none">{nApts}</div>
                    </div>
                  </div>
                </div>

                {/* Master */}
                <div className="font-semibold text-slate-800 text-sm leading-tight line-clamp-2 min-h-[2.5rem]">
                  {m?.couple_display_name || <span className="text-slate-400 italic">(master inconnu)</span>}
                </div>

                {/* NEQ + Type */}
                <div className="mt-2 space-y-0.5 min-h-[34px]">
                  {it.neq && (
                    <div className="text-[11px] text-slate-500 font-mono truncate">
                      <span className="text-slate-400">NEQ&nbsp;:</span> {it.neq}
                    </div>
                  )}
                  {it.accounting_type && (
                    <div className="text-[11px] text-slate-500 truncate">
                      <span className="text-slate-400">Type&nbsp;:</span> {it.accounting_type}
                    </div>
                  )}
                </div>

                {/* Pied : date + actions */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                  <div className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                    <Calendar className="w-3 h-3" /> {new Date(it.created_at).toLocaleDateString("fr-CA")}
                  </div>

                  {/* Batch #14.128 — Actions selon état (archivé vs actif) */}
                  {isArchived ? (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleRestore(it, masterName); }}
                        disabled={isBusy}
                        className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 disabled:opacity-50 p-1 rounded inline-flex items-center gap-1 text-[11px] font-medium"
                        data-testid={`immeuble-list-restore-btn-${it.id}`}
                        title="Réactiver ce dossier immeuble"
                      >
                        {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
                        Restaurer
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleHardDelete(it, masterName); }}
                        disabled={isBusy}
                        className="text-rose-600 hover:text-rose-800 hover:bg-rose-50 disabled:opacity-50 p-1 rounded inline-flex items-center gap-1 text-[11px] font-medium"
                        data-testid={`immeuble-list-delete-btn-${it.id}`}
                        title="Supprimer définitivement (irréversible)"
                      >
                        <Trash2 className="w-3 h-3" />
                        Supprimer
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleArchive(it, masterName); }}
                        disabled={isBusy}
                        className="text-amber-600 hover:text-amber-800 hover:bg-amber-50 disabled:opacity-50 p-1 rounded"
                        data-testid={`immeuble-list-archive-btn-${it.id}`}
                        title="Archiver ce dossier (le cacher de la liste)"
                      >
                        {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
                      </button>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-500 group-hover:translate-x-0.5 transition-all" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showWizard && (
        <CreateImmeubleDossierWizard
          onClose={() => setShowWizard(false)}
          onCreated={(newId) => {
            setShowWizard(false);
            navigate(`/admin/immeuble-dossiers/${newId}`);
          }}
        />
      )}
    </div>
  );
}

// ============================================================
//  Wizard 2 étapes — création d'un dossier immeuble déclaratif
//  (Option 3a : champs globaux uniquement, propriétés ajoutées à l'Étape 3)
// ============================================================
function CreateImmeubleDossierWizard({ onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [masters, setMasters] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedMasterId, setSelectedMasterId] = useState("");
  const [loadingMasters, setLoadingMasters] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    neq: "",
    accounting_type: "",
    fiscal_year_end: "",
    tps_registered: false,
    tvq_registered: false,
    incorporated: false,
    holding_structure: "",
    notes: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/masters`, { headers: authH() });
        setMasters(r.data?.items || []);
      } catch {
        setMasters([]);
      } finally {
        setLoadingMasters(false);
      }
    })();
  }, []);

  const selectedMaster = masters.find((m) => m.id === selectedMasterId);
  const filtered = masters.filter((m) => !search ||
    (m.couple_display_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (m.primary_email || "").toLowerCase().includes(search.toLowerCase()));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        master_id: selectedMasterId,
        neq: form.neq || null,
        accounting_type: form.accounting_type || null,
        fiscal_year_end: form.fiscal_year_end || null,
        tps_registered: form.tps_registered,
        tvq_registered: form.tvq_registered,
        incorporated: form.incorporated,
        holding_structure: form.holding_structure || null,
        notes: form.notes || null,
      };
      const r = await axios.post(`${API}/immeuble-dossiers`, payload, { headers: authH() });
      toast.success("Dossier immeuble créé");
      onCreated(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur création");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="immeuble-wizard-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[92vh] flex flex-col">
        <div className="px-5 py-3 border-b flex items-center justify-between bg-slate-50">
          <div>
            <h3 className="font-bold">
              {step === 1 ? "Nouveau dossier immeuble — Étape 1/2" : "Nouveau dossier immeuble — Étape 2/2"}
            </h3>
            <p className="text-[11px] text-slate-500">
              {step === 1
                ? "Sélectionnez le Master à rattacher (lien permanent, non modifiable après création)."
                : `Master sélectionné : ${selectedMaster?.couple_display_name || "?"}`}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded" data-testid="immeuble-wizard-close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {step === 1 && (
          <>
            <div className="p-4 border-b">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un Master par nom ou courriel…"
                  className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
                  data-testid="immeuble-wizard-master-search"
                />
              </div>
            </div>
            <div className="overflow-y-auto flex-1 px-2 py-1" data-testid="immeuble-wizard-master-list">
              {loadingMasters ? (
                <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400 inline" /></div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-10 text-sm text-slate-400 italic">
                  Aucun Master trouvé. <Link to="/admin/masters" className="text-emerald-600 underline">Créer un Master</Link>
                </div>
              ) : (
                filtered.map((m) => (
                  <label
                    key={m.id}
                    onClick={() => setSelectedMasterId(m.id)}
                    className={`flex items-center gap-3 px-3 py-2.5 border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${selectedMasterId === m.id ? "bg-emerald-50" : ""}`}
                    data-testid={`immeuble-wizard-master-${m.id}`}
                  >
                    <input
                      type="radio"
                      name="master-pick"
                      checked={selectedMasterId === m.id}
                      onChange={() => setSelectedMasterId(m.id)}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-800 text-sm truncate">{m.couple_display_name || "(sans nom)"}</div>
                      <div className="text-[11px] text-slate-500">{m.primary_name} {m.secondary_name ? `+ ${m.secondary_name}` : ""}{m.primary_email ? ` · ${m.primary_email}` : ""}</div>
                    </div>
                  </label>
                ))
              )}
            </div>
            <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Annuler</button>
              <button
                onClick={() => setStep(2)}
                disabled={!selectedMasterId}
                className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 text-white disabled:opacity-40 inline-flex items-center gap-1.5"
                data-testid="immeuble-wizard-next"
              >
                Suivant <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
              {/* Bandeau Master sélectionné */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs">
                <div className="font-semibold text-slate-700">{selectedMaster?.couple_display_name}</div>
                <div className="text-slate-500 mt-0.5">
                  {selectedMaster?.primary_name}
                  {selectedMaster?.secondary_name ? ` + ${selectedMaster.secondary_name}` : ""}
                </div>
                <div className="text-[10px] text-slate-400 italic mt-1 inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Les champs ci-dessous appartiennent au dossier immeuble — pas au Master.
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="text-xs font-semibold text-slate-700">NEQ — Numéro d'Entreprise du Québec</span>
                  <input
                    type="text"
                    value={form.neq}
                    onChange={(e) => setForm({ ...form, neq: e.target.value })}
                    maxLength={50}
                    placeholder="1234567890"
                    className="w-full mt-1 px-3 py-2 border rounded-lg font-mono text-sm"
                    data-testid="immeuble-wizard-neq"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-xs font-semibold text-slate-700">Type comptable</span>
                  <input
                    type="text"
                    value={form.accounting_type}
                    onChange={(e) => setForm({ ...form, accounting_type: e.target.value })}
                    maxLength={100}
                    placeholder="ex: personnel, société, fiducie…"
                    className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                    data-testid="immeuble-wizard-accounting-type"
                  />
                </label>
              </div>

              <label className="block text-sm">
                <span className="text-xs font-semibold text-slate-700">Fin d'exercice (MM-DD ou YYYY-MM-DD)</span>
                <input
                  type="text"
                  value={form.fiscal_year_end}
                  onChange={(e) => setForm({ ...form, fiscal_year_end: e.target.value })}
                  maxLength={10}
                  placeholder="12-31"
                  className="w-full mt-1 px-3 py-2 border rounded-lg font-mono text-sm"
                  data-testid="immeuble-wizard-fiscal-year-end"
                />
              </label>

              <div className="grid grid-cols-3 gap-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer border rounded-lg px-3 py-2 hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={form.tps_registered}
                    onChange={(e) => setForm({ ...form, tps_registered: e.target.checked })}
                    data-testid="immeuble-wizard-tps"
                  />
                  Enregistré TPS
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer border rounded-lg px-3 py-2 hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={form.tvq_registered}
                    onChange={(e) => setForm({ ...form, tvq_registered: e.target.checked })}
                    data-testid="immeuble-wizard-tvq"
                  />
                  Enregistré TVQ
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer border rounded-lg px-3 py-2 hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={form.incorporated}
                    onChange={(e) => setForm({ ...form, incorporated: e.target.checked })}
                    data-testid="immeuble-wizard-incorporated"
                  />
                  Incorporé
                </label>
              </div>

              <label className="block text-sm">
                <span className="text-xs font-semibold text-slate-700">Structure / Holding</span>
                <textarea
                  rows={2}
                  value={form.holding_structure}
                  onChange={(e) => setForm({ ...form, holding_structure: e.target.value })}
                  maxLength={500}
                  placeholder="ex: Holding XYZ inc. → Société exploitante immobilière"
                  className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                  data-testid="immeuble-wizard-holding"
                />
              </label>

              <label className="block text-sm">
                <span className="text-xs font-semibold text-slate-700">Notes internes</span>
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  maxLength={2000}
                  className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                  data-testid="immeuble-wizard-notes"
                />
              </label>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                💡 Le dossier sera créé <b>vide</b>. Les propriétés / appartements / locataires seront ajoutables
                depuis la page de détail (CRUD complet disponible à l'Étape 3).
              </div>
            </div>

            <div className="px-5 py-3 border-t bg-slate-50 flex justify-between gap-2">
              <button onClick={() => setStep(1)} className="text-xs text-slate-500 hover:text-slate-700" data-testid="immeuble-wizard-back">
                ← Étape précédente
              </button>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Annuler</button>
                <button
                  onClick={submit}
                  disabled={saving}
                  className="px-4 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1.5"
                  data-testid="immeuble-wizard-submit"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  Créer le dossier
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
